import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import "./ClientDashboard.css";

const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(n ?? 0);

const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || "";
};

const ClientDashboard = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.quotes(), api.invoices(), api.projects()])
            .then(([qRes, iRes, pRes]) => {
                setQuotes(qRes.quotes?.data || []);
                setInvoices(iRes.invoices?.data || []);
                setProjects(pRes.projects || []);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <p style={{ color: "var(--color-text-muted)" }}>
                {t("common.loading")}
            </p>
        );

    const pendingQuotes = quotes.filter((q) => q.status === "sent");
    const unpaidInvoices = invoices.filter(
        (inv) => inv.status === "sent" || inv.status === "overdue",
    );

    return (
        <div className="client-dash">
            <div className="section-header" style={{ marginBottom: 28 }}>
                <h2>
                    {t("dashboard.welcome")}, {user?.name?.split(" ")[0]}
                </h2>
            </div>

            {(pendingQuotes.length > 0 || unpaidInvoices.length > 0) && (
                <div className="client-dash__alerts">
                    {pendingQuotes.map((q) => (
                        <Link
                            key={`q-${q.id}`}
                            to={`/dashboard/quotes/${q.id}`}
                            className="client-dash__alert client-dash__alert--quote"
                        >
                            <span className="client-dash__alert-tag">
                                Action needed
                            </span>
                            <span>
                                Quote <strong>{q.title}</strong> (
                                {formatCAD(q.total)}) is awaiting your
                                response.
                            </span>
                            <span className="client-dash__alert-cta">
                                Review →
                            </span>
                        </Link>
                    ))}
                    {unpaidInvoices.map((inv) => (
                        <Link
                            key={`i-${inv.id}`}
                            to={`/dashboard/invoices/${inv.id}`}
                            className={`client-dash__alert ${inv.status === "overdue" ? "client-dash__alert--overdue" : "client-dash__alert--invoice"}`}
                        >
                            <span className="client-dash__alert-tag">
                                {inv.status === "overdue"
                                    ? "Overdue"
                                    : "Unpaid"}
                            </span>
                            <span>
                                Invoice <strong>{inv.invoice_number}</strong> (
                                {formatCAD(inv.total)}) is due{" "}
                                {new Date(inv.due_date).toLocaleDateString(
                                    "en-CA",
                                )}
                                .
                            </span>
                            <span className="client-dash__alert-cta">
                                View →
                            </span>
                        </Link>
                    ))}
                </div>
            )}

            <section className="client-dash__section">
                <div className="client-dash__section-header">
                    <h3>{t("dashboard.myQuotes")}</h3>
                    <Link
                        to="/dashboard/messages"
                        className="client-dash__section-link"
                    >
                        Request a quote →
                    </Link>
                </div>
                {quotes.length === 0 ? (
                    <p className="client-dash__empty">{t("quote.noQuotes")}</p>
                ) : (
                    <div className="client-dash__list">
                        {quotes.map((q) => (
                            <div key={q.id} className="client-dash__item">
                                <div className="client-dash__item-info">
                                    <span className="client-dash__item-title">
                                        {q.title}
                                    </span>
                                    <span className="client-dash__item-meta">
                                        {t("quote.created")}:{" "}
                                        {new Date(
                                            q.created_at,
                                        ).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="client-dash__item-right">
                                    <StatusBadge status={q.status} />
                                    <span className="client-dash__item-total">
                                        {formatCAD(q.total)}
                                    </span>
                                    <Link
                                        to={`/dashboard/quotes/${q.id}`}
                                        className="btn btn--ghost btn--sm"
                                    >
                                        {t("common.view")}
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {projects.length > 0 && (
                <section className="client-dash__section">
                    <div className="client-dash__section-header">
                        <h3>My Projects</h3>
                        <Link
                            to="/dashboard/projects"
                            className="client-dash__section-link"
                        >
                            View all →
                        </Link>
                    </div>
                    <div className="client-dash__list">
                        {projects.slice(0, 3).map((p) => {
                            const stages = p.stages || [];
                            const done = stages.filter(
                                (s) => s.status === "completed",
                            ).length;
                            const preview = stripHtml(p.description);
                            return (
                                <div key={p.id} className="client-dash__item">
                                    <div className="client-dash__item-info">
                                        <span className="client-dash__item-title">
                                            {p.title}
                                        </span>
                                        {preview && (
                                            <span className="client-dash__item-meta">
                                                {preview.slice(0, 80)}
                                                {preview.length > 80
                                                    ? "…"
                                                    : ""}
                                            </span>
                                        )}
                                        {stages.length > 0 && (
                                            <span className="client-dash__progress">
                                                <span className="client-dash__progress-bar">
                                                    <span
                                                        className="client-dash__progress-fill"
                                                        style={{
                                                            width: `${(done / stages.length) * 100}%`,
                                                        }}
                                                    />
                                                </span>
                                                <span className="client-dash__progress-label">
                                                    {done}/{stages.length}{" "}
                                                    stages
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="client-dash__item-right">
                                        <StatusBadge status={p.status} />
                                        <Link
                                            to={`/dashboard/projects/${p.id}`}
                                            className="btn btn--ghost btn--sm"
                                        >
                                            {t("common.view")}
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            <section className="client-dash__section">
                <div className="client-dash__section-header">
                    <h3>{t("dashboard.myInvoices")}</h3>
                </div>
                {invoices.length === 0 ? (
                    <p className="client-dash__empty">
                        {t("invoice.noInvoices")}
                    </p>
                ) : (
                    <div className="client-dash__list">
                        {invoices.map((inv) => (
                            <div key={inv.id} className="client-dash__item">
                                <div className="client-dash__item-info">
                                    <span className="client-dash__item-title client-dash__item-title--mono">
                                        {inv.invoice_number}
                                    </span>
                                    <span className="client-dash__item-meta">
                                        {t("invoice.dueDate")}:{" "}
                                        {new Date(
                                            inv.due_date,
                                        ).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="client-dash__item-right">
                                    <StatusBadge status={inv.status} />
                                    <span className="client-dash__item-total">
                                        {formatCAD(inv.total)}
                                    </span>
                                    <Link
                                        to={`/dashboard/invoices/${inv.id}`}
                                        className="btn btn--ghost btn--sm"
                                    >
                                        {t("common.view")}
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default ClientDashboard;
